import useApi from '../../../hooks/useApi';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Platform-wide totals as of right now — there's no historical snapshot
// behind these numbers yet, so the cards show counts only, no invented
// trend percentages.
const METRIC_CARDS = [
  { key: 'totalUsers', label: 'Total Users', icon: '👥' },
  { key: 'activeUsers', label: 'Active Users', icon: '👤' },
  { key: 'consultations', label: 'Consultations', icon: '💬' },
  { key: 'wasteRequests', label: 'Waste Requests', icon: '🗑️' },
  { key: 'wasteProcessed', label: 'Waste Processed (kg)', icon: '♻️' },
];

function Dashboard() {
  const { data: metrics, loading, error } = useApi('/analytics/dashboard');

  // The chart needs a monthly breakdown of waste requests, which
  // /analytics/dashboard doesn't provide (it's a single current total).
  // Until a real monthly-series endpoint exists, show the one true number
  // we have instead of fabricating a 12-month split.
  const chartData = {
    labels: ['This period'],
    datasets: [
      {
        label: 'Waste Requests',
        data: [metrics?.wasteRequests || 0],
        backgroundColor: '#3B82F6',
        borderRadius: 4,
        barThickness: 48,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, grid: { color: '#E5E7EB' }, ticks: { precision: 0 } },
    },
    plugins: { legend: { display: false } },
  };

  const targetWasteProcessed = 1000; // kg — internal goal, adjust as needed
  const wasteProcessed = metrics?.wasteProcessed || 0;
  const progress = Math.min((wasteProcessed / targetWasteProcessed) * 100, 100);
  const remaining = Math.max(targetWasteProcessed - wasteProcessed, 0);

  return (
    <div className="p-5 dark:bg-gray-900 min-h-screen">
      {loading ? (
        <div className="text-center dark:text-white py-20">Loading...</div>
      ) : error ? (
        <div className="text-red-500 text-center py-20">{error}</div>
      ) : (
        <>
          {/* Cards Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-6">
            {METRIC_CARDS.map((metric) => (
              <div
                key={metric.key}
                className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-md flex items-center space-x-4"
              >
                <div className="text-3xl">{metric.icon}</div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{metric.label}</h3>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {(metrics?.[metric.key] ?? 0).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Waste Requests */}
            <div className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4 dark:text-white">Waste Requests</h3>
              <div className="h-64">
                <Bar data={chartData} options={chartOptions} />
              </div>
            </div>

            {/* Waste Processed Target */}
            <div className="bg-white dark:bg-gray-800 p-5 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold mb-4 dark:text-white">Waste Processed Target</h3>
              <div className="flex flex-col items-center">
                <div className="w-40 h-40 mb-4">
                  <CircularProgressbar
                    value={progress}
                    text={`${progress.toFixed(0)}%`}
                    styles={buildStyles({
                      pathColor: '#3B82F6',
                      textColor: '#3B82F6',
                      trailColor: '#E5E7EB',
                    })}
                  />
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 text-center">
                  {wasteProcessed.toLocaleString()} kg processed toward a {targetWasteProcessed.toLocaleString()} kg
                  target.
                </p>
                <div className="flex justify-between w-full mt-4">
                  <div>
                    <p className="text-sm dark:text-white">Target</p>
                    <p className="text-lg font-bold text-gray-700 dark:text-gray-200">
                      {targetWasteProcessed.toLocaleString()} kg
                    </p>
                  </div>
                  <div>
                    <p className="text-sm dark:text-white">Processed</p>
                    <p className="text-lg font-bold text-green-500">{wasteProcessed.toLocaleString()} kg</p>
                  </div>
                  <div>
                    <p className="text-sm dark:text-white">Remaining</p>
                    <p className="text-lg font-bold text-amber-500">{remaining.toLocaleString()} kg</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;